import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../../utils/abortController';
import useUser from '../../../hooks/useUser';
import useSessionExpire from '../../../hooks/useSessionExpire';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Backdrop from '@mui/material/Backdrop';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Loader from '../../../components/Loader';
import VirtualizedAutocomplete from '../../../components/Autocomplete';
import dayjs from 'dayjs';
import axios from 'axios';
import { roles } from '../../../utils/roles';
import withAuth from '../../../lib/withAuth';

const SBLCFormV2 = ({
    selectedSBLC = null,
    isLoading = false,
    toggleDisplayData = false,
    setIsLoading = () => { },
    setShowAlert = () => { },
    setAlertMessage = () => { },
    setAlertType = () => { },
    setShowSBLCForm = () => { },
    setSelectedSBLC = () => { },
    setToggleDisplayData = () => { },
    logOut = () => { },
}) => {
    const { pathname } = useLocation();

    /* AUTHENTICATION VARIABLES */
    const currentUser = useUser();

    /* SESSION EXPIRY VARIABLES */
    const triggerSessionExpire = useSessionExpire();

    /* LIST VARIABLES */
    const [banks, setBanks] = useState([]);
    const [customerVendors, setCustomerVendors] = useState([]);
    const [supplierVendors, setSupplierVendors] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    /* STATIC OPTIONS */
    const sblcLegOptions = [
        { id: 'Selling', value: 'Selling' },
        { id: 'Buying', value: 'Buying' },
    ];

    const tenorTypeOptions = ['Sight', 'Usance'];

    const allFromDateOptions = useMemo(() => [
        { id: 'bl_date', value: 'BL Date' },
        { id: 'acceptance', value: 'Acceptance' },
    ], []);

    /* FORM SCHEMA */
    const dataSchema = {
        sblc_number: null,
        bank_id: null,
        sblc_leg: null,
        vendor_id: null,
        amount: null,
        available_amount: null,
        currency_id: null,
        tenor_type: null,
        usance_days: null,
        from_date_field: null,
        validity_date: null,
        reminder_days_before: 5,
        notes: '',
    };

    const [formData, setFormData] = useState(dataSchema);
    const [isExistingRecord, setIsExistingRecord] = useState(false);

    /* DIALOG VARIABLES */
    const [showAutoRaiseConfirmDialog, setShowAutoRaiseConfirmDialog] = useState(false);
    const [pendingAutoRaiseValue, setPendingAutoRaiseValue] = useState(false);

    /* REFS */
    const formRef = useRef(null);
    const submitButtonRef = useRef(null);

    /* DERIVED */
    const tenorTypeUpper = (formData.tenor_type || '').toUpperCase();
    const isUsance = tenorTypeUpper === 'USANCE';
    const legId = formData.sblc_leg?.id;
    const counterParties = legId === 'Buying'
        ? supplierVendors
        : legId === 'Selling'
            ? customerVendors
            : [];

    /* SHARED ERROR HANDLER */
    const handleApiError = (error, label = 'Api') => {
        console.log(`${label}: `, error);
        if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return;

        const status = error?.response?.status;

        if (status === 403) {
            triggerSessionExpire();
        } else {
            setAlertMessage(
                status === 401
                    ? 'Unauthorized access. You do not have the required permissions to perform this action.'
                    : status === 429
                        ? 'Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator.'
                        : 'An error occurred while processing your request. Please try again later or contact the site administrator.'
            );
            setAlertType('error');
            setShowAlert(true);
            if (status === 429) setTimeout(logOut, 3000);
        }
    };

    /* FETCH LISTS */
    useEffect(() => {
        const signal = abortController.signal;

        axios({ method: 'post', url: '/api/GetBankList', data: { pathname }, signal })
            .then(({ status, data }) => { if (status === 200) setBanks(data?.banks || []); })
            .catch((e) => handleApiError(e, 'Get Banks'));

        axios({ method: 'post', url: '/api/GetCurrencyList', data: { pathname }, signal })
            .then(({ status, data }) => { if (status === 200) setCurrencies(data?.currencies || []); })
            .catch((e) => handleApiError(e, 'Get Currencies'));

        axios({ method: 'post', url: '/api/GetCounterPartyList', data: { pathname }, signal })
            .then(({ status, data }) => {
                if (status === 200) {
                    setCustomerVendors(data?.customer_vendors || []);
                    setSupplierVendors(data?.supplier_vendors || []);
                }
            })
            .catch((e) => handleApiError(e, 'Get Vendors'));

        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    /* FETCH SINGLE SBLC FOR EDITING */
    useEffect(() => {
        if (!selectedSBLC) return;

        const fetchSBLCData = async () => {
            setIsLoading(true);
            setShowAlert(false);
            setAlertMessage('');
            setAlertType('');

            await axios({
                method: 'post',
                url: '/api/v2/GetSingleSBLC',
                data: { sblc_id: selectedSBLC.id },
                signal: abortController.signal,
            })
                .then(({ status, data = {} }) => {
                    setIsLoading(false);

                    if (status === 200) {
                        const record = data?.sblc;

                        if (!record) return;

                        setIsExistingRecord(true);

                        const normaliseDate = (val) =>
                            val ? dayjs(val).format('MM/DD/YYYY') : null;

                        const resolvedVendorType = record.sblc_leg
                            ? sblcLegOptions.find((o) => o.id === record.sblc_leg) ?? null
                            : null;

                        const resolvedFromDateField = record.from_date_field
                            ? allFromDateOptions.find((o) => o.id === record.from_date_field) ?? null
                            : null;

                        setFormData({
                            ...dataSchema,
                            sblc_number: record.sblc_number ?? null,
                            bank_id: record.bank_id ?? null,
                            sblc_leg: resolvedVendorType,
                            vendor_id: record.vendor_id ?? null,
                            amount: record.amount ?? null,
                            available_amount: record.available_amount ?? record.amount ?? null,
                            currency_id: record.currency_id ?? null,
                            tenor_type: record.tenor_type ?? null,
                            usance_days: record.usance_days ?? null,
                            from_date_field: resolvedFromDateField,
                            validity_date: normaliseDate(record.validity_date),
                            reminder_days_before: record.reminder_days_before ?? 5,
                            notes: record.notes ?? '',
                        });
                    } else {
                        setAlertMessage(
                            'An error occurred while processing your request. Please try again later or contact the site administrator.'
                        );
                        setAlertType('error');
                        setShowAlert(true);
                    }
                })
                .catch((e) => {
                    setIsLoading(false);
                    handleApiError(e, 'GetSingleSBLC');
                });
        };

        fetchSBLCData();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [selectedSBLC]);

    /* HELPERS */
    const convertToDayJsObject = (date) =>
        date ? dayjs(date, 'MM/DD/YYYY') : null;

    const handleFormValidation = () => {
        const formElements = formRef?.current?.elements;

        Array.from(formElements).forEach((element) => {
            if (element.nodeName === 'INPUT' && element.type !== 'submit') {
                const { value } = element;
                const isRequired = element.hasAttribute('required');
                const isAutocomplete = element?.className?.includes('MuiAutocomplete');

                if (!isAutocomplete && isRequired && !value?.trim()) {
                    element.setCustomValidity('Please fill in this field.');
                } else {
                    element.setCustomValidity('');
                }
            }
        });

        formRef.current.reportValidity();
    };

    /* FIELD CHANGE HANDLER */
    const handleChange = (field, value, isDate = false) => {
        let newValue = value;

        if (isDate && newValue) {
            newValue = dayjs(newValue).format('MM/DD/YYYY');
        }

        if (field === 'amount') {
            newValue = String(value).replace(/[^0-9.]/g, '');
            if (newValue.includes('.')) {
                const [intPart, decPart] = newValue.split('.');
                newValue = `${intPart}.${decPart.substring(0, 2)}`;
            }

            setFormData((prev) => ({
                ...prev,
                amount: newValue === '' ? null : newValue,
                available_amount: newValue === '' ? null : newValue,
                isUpdated: true,
            }));
            return;
        }

        if (field === 'reminder_days_before') {
            newValue = String(value).replace(/[^0-9]/g, '');
        }

        // Changing sblc_leg type clears vendor_id so user re-picks
        if (field === 'sblc_leg') {
            setFormData((prev) => ({
                ...prev,
                sblc_leg: value ?? null,
                vendor_id: null,
                isUpdated: true,
            }));
            return;
        }

        // Changing tenor type clears usance sub-fields
        if (field === 'tenor_type') {
            setFormData((prev) => ({
                ...prev,
                tenor_type: value ?? null,
                usance_days: null,
                from_date_field: null,
                isUpdated: true,
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [field]:
                typeof newValue === 'string'
                    ? newValue.trim() === '' ? null : newValue
                    : newValue ?? null,
            isUpdated: true,
        }));
    };

    const handleDaysChange = (e) => {
        const raw = e.target.value;
        if (raw === '') { handleChange('usance_days', null); return; }
        const v = parseInt(raw);
        if (!isNaN(v) && v >= 0) handleChange('usance_days', v);
    };

    const handleConfirmAutoRaise = () => {
        setShowAutoRaiseConfirmDialog(false);
        setFormData((prev) => ({ ...prev, isUpdated: true }));
    };

    /* SUBMIT */
    const handleSubmitWrapper = () => {
        const isValid = formRef?.current?.checkValidity();
        if (isValid) {
            submitButtonRef?.current?.click();
        } else {
            handleFormValidation();
        }
    };

    const handleSubmit = async (event) => {
        event?.preventDefault();

        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage('');
        setAlertType('');

        const transformObject = (obj, seen = new Set()) => {
            if (seen.has(obj)) return obj;
            if (typeof obj !== 'object' || obj === null) return obj;
            seen.add(obj);

            if (Array.isArray(obj)) {
                return obj.map((item) =>
                    typeof item === 'object' && item !== null
                        ? item.hasOwnProperty('value') ? item.id : transformObject(item, seen)
                        : item
                );
            }

            return Object.fromEntries(
                Object.entries(obj).map(([key, val]) => [
                    key,
                    typeof val === 'object' && val !== null
                        ? Array.isArray(val)
                            ? transformObject(val, seen)
                            : val.hasOwnProperty('value')
                                ? val.id
                                : transformObject(val, seen)
                        : val,
                ])
            );
        };

        const payload = transformObject({
            sblc_id: selectedSBLC?.id ?? null,
            sblc_number: formData.sblc_number,
            bank_id: formData.bank_id,
            sblc_leg: formData.sblc_leg,
            vendor_id: formData.vendor_id,
            amount: formData.amount,
            available_amount: formData.available_amount,
            currency_id: formData.currency_id,
            tenor_type: formData.tenor_type,
            usance_days: isUsance ? formData.usance_days : null,
            from_date_field: isUsance ? formData.from_date_field : null,
            validity_date: formData.validity_date,
            reminder_days_before: formData.reminder_days_before,
            notes: formData.notes,
            user_id: currentUser?.id,
        });

        const apiUrl = isExistingRecord ? '/api/v2/EditSBLC' : '/api/v2/AddSBLC';

        await axios({
            method: 'post',
            url: apiUrl,
            headers: { 'Content-Type': 'application/json' },
            data: payload,
            signal: abortController.signal,
        })
            .then(({ status }) => {
                setIsLoading(false);

                if (status === 200) {
                    setAlertMessage('Data saved successfully.');
                    setAlertType('success');
                    setShowAlert(true);

                    setTimeout(() => {
                        handleReset();
                        handleBackPress();
                    }, 1000);
                } else {
                    setAlertMessage(
                        'An error occurred while processing your request. Please try again later or contact the site administrator.'
                    );
                    setAlertType('error');
                    setShowAlert(true);
                }
            })
            .catch((e) => {
                setIsLoading(false);
                handleApiError(e, isExistingRecord ? 'EditSBLC' : 'AddSBLC');
            });
    };

    /* RESET / BACK */
    const handleReset = () => {
        setFormData(dataSchema);
        setIsExistingRecord(false);
    };

    const handleBackPress = () => {
        setShowSBLCForm(false);
        setSelectedSBLC(null);
        setToggleDisplayData(!toggleDisplayData);
    };

    const handleCloseDialog = () => {
        setShowAutoRaiseConfirmDialog(false);
    };

    /* ═══════════════════════════════════════════════════════════════════════════ */
    return (
        <>
            {/* PAGE HEADER */}
            <div className="d-flex justify-content-start align-items-center gap-2">
                <IconButton size="small" color="primary" sx={{ boxShadow: 3 }} onClick={handleBackPress}>
                    <ArrowBackIcon fontSize="medium" />
                </IconButton>
                <h4 className="page-heading m-0">{selectedSBLC ? 'Edit SBLC' : 'Add SBLC'}</h4>
            </div>

            {/* LOADING BACKDROP */}
            <Backdrop
                sx={{ color: '#fff', position: 'absolute', height: '100%', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <Loader />
            </Backdrop>

            {/* FORM */}
            <form
                ref={formRef}
                className="deal-form multi-form-container mt-1 p-2"
                onSubmit={handleSubmit}
            >
                <div className="d-flex flex-column gap-3">
                    <Grid container spacing={"10px"} className="align-items-start autocomplete">

                        {/* ════════════════════════════════════════════════
                            SECTION 1 — Basic Information
                        ════════════════════════════════════════════════ */}
                        <Grid size={{ xs: 12 }}>
                            <div className="w-100 mt-1">
                                <h6 className="mb-0">Basic Information</h6>
                            </div>
                        </Grid>

                        {/* SBLC Number */}
                        <Grid size={{ xs: 3 }} className="form-text-field">
                            <TextField
                                label="SBLC Number"
                                id="sblc_number"
                                value={formData.sblc_number || ''}
                                required
                                onChange={(e) => handleChange('sblc_number', e.target.value)}
                            />
                        </Grid>

                        {/* Issuing Bank */}
                        <Grid size={{ xs: 3 }}>
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={true}
                                isWritable={true}
                                isRequired={true}
                                filterOn="Issuing Bank"
                                options={banks}
                                selectedOptions={formData.bank_id || null}
                                handleSelectChange={(_, v) => handleChange('bank_id', v)}
                            />
                        </Grid>

                        {/* Vendor Type */}
                        <Grid size={{ xs: 3 }}>
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={true}
                                isWritable={true}
                                isRequired={true}
                                filterOn="SBLC Leg"
                                options={sblcLegOptions}
                                selectedOptions={formData.sblc_leg || null}
                                handleSelectChange={(_, v) => handleChange('sblc_leg', v)}
                            />
                        </Grid>

                        {/* Vendor */}
                        <Grid size={{ xs: 3 }}>
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={true}
                                isWritable={true}
                                isRequired={true}
                                filterOn="Vendor"
                                options={counterParties}
                                selectedOptions={formData.vendor_id || null}
                                handleSelectChange={(_, v) => handleChange('vendor_id', v)}
                            />
                        </Grid>

                        {/* Validity Date */}
                        <Grid size={{ xs: 3 }} className="form-text-field">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    inputFormat="MM/DD/YYYY"
                                    label="Validity Date"
                                    disableMaskedInput
                                    className="date-picker w-100"
                                    value={convertToDayJsObject(formData.validity_date)}
                                    onChange={(d) => handleChange('validity_date', d, true)}
                                    onAccept={(d) => handleChange('validity_date', d, true)}
                                    slotProps={{
                                        actionBar: { actions: ['clear'] },
                                        textField: { required: true, InputProps: { size: 'small', disabled: true } },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* Amount */}
                        <Grid size={{ xs: 3 }} className="form-text-field">
                            <TextField
                                label="Amount"
                                id="amount"
                                type="number"
                                required
                                size="small"
                                fullWidth
                                value={formData.amount ?? ''}
                                onChange={(e) => handleChange('amount', e.target.value)}
                                slotProps={{
                                    input: {
                                        inputProps: { min: 0, max: 2147483647, step: '0.01' },
                                    },
                                    inputLabel: { shrink: formData.amount != null },
                                }}
                                onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key))
                                        e.preventDefault();
                                }}
                            />
                        </Grid>

                        {/* Available Amount (read-only) */}
                        <Grid size={{ xs: 3 }} className="form-text-field">
                            <TextField
                                label="Available Amount"
                                id="available_amount"
                                type="number"
                                disabled
                                value={formData.available_amount ?? ''}
                                slotProps={{
                                    inputLabel: { shrink: formData.available_amount != null },
                                }}
                            />
                        </Grid>

                        {/* Currency */}
                        <Grid size={{ xs: 3 }}>
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={true}
                                isWritable={true}
                                isRequired={true}
                                filterOn="Currency"
                                options={currencies}
                                selectedOptions={formData.currency_id || null}
                                handleSelectChange={(_, v) => handleChange('currency_id', v)}
                            />
                        </Grid>

                        {/* ════════════════════════════════════════════════
                            SECTION 2 — Tenor
                        ════════════════════════════════════════════════ */}
                        <Grid size={{ xs: 12 }}>
                            <hr />
                            <div className="w-100">
                                <h6 className="mb-0">Tenor</h6>
                            </div>
                        </Grid>

                        {/* Tenor Type — Sight / Usance */}
                        <Grid size={{ xs: 3 }}>
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={false}
                                isWritable={true}
                                filterOn="Tenor"
                                options={tenorTypeOptions}
                                selectedOptions={formData.tenor_type || null}
                                handleSelectChange={(_, v) => handleChange('tenor_type', v)}
                            />
                        </Grid>

                        {/* Usance Days — only when Usance is selected */}
                        {isUsance && (
                            <>
                                <Grid size={{ xs: 3 }} className="form-text-field">
                                    <TextField
                                        label="Usance Days"
                                        id="usance_days"
                                        type="number"
                                        required
                                        size="small"
                                        fullWidth
                                        value={formData.usance_days ?? ''}
                                        onChange={handleDaysChange}
                                        slotProps={{
                                            input: {
                                                inputProps: { min: 0 },
                                            },
                                            inputLabel: { shrink: formData.usance_days != null },
                                        }}
                                        onKeyDown={(e) => {
                                            if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key))
                                                e.preventDefault();
                                        }}
                                    />
                                </Grid>

                                {/* Calculate From */}
                                <Grid size={{ xs: 3 }}>
                                    <VirtualizedAutocomplete
                                        isMultiple={false}
                                        isObject={true}
                                        isWritable={true}
                                        isRequired={true}
                                        filterOn="Calculate From"
                                        options={allFromDateOptions}
                                        selectedOptions={formData.from_date_field || null}
                                        handleSelectChange={(_, v) => handleChange('from_date_field', v)}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* ════════════════════════════════════════════════
                            SECTION 3 — Maturity & Reminders
                        ════════════════════════════════════════════════ */}
                        <Grid size={{ xs: 12 }}>
                            <hr />
                            <div className="w-100">
                                <h6 className="mb-0">Maturity &amp; Reminders</h6>
                                <p className="text-muted small mb-0">
                                    A reminder email is sent automatically the specified number of days before the maturity date.
                                    If auto-raise is enabled, the system will also raise the claim request on the maturity date.
                                </p>
                            </div>
                        </Grid>

                        {/* Reminder Days Before */}
                        <Grid size={{ xs: 3 }} className="form-text-field">
                            <TextField
                                label="Reminder Days Before Maturity"
                                id="reminder_days_before"
                                type="number"
                                required
                                value={formData.reminder_days_before}
                                onChange={(e) => handleChange('reminder_days_before', e.target.value)}
                                slotProps={{
                                    input: {
                                        min: 1,
                                        max: 30,
                                        sx: {
                                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                            '& input[type=number]': { MozAppearance: 'textfield' },
                                        },
                                    },
                                }}
                                onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-', '.', 'ArrowUp', 'ArrowDown'].includes(e.key))
                                        e.preventDefault();
                                }}
                            />
                        </Grid>

                        {/* ════════════════════════════════════════════════
                            SECTION 4 — Notes
                        ════════════════════════════════════════════════ */}
                        <Grid size={{ xs: 12 }}>
                            <hr />
                            <div className="w-100">
                                <h6 className="mb-0">Notes</h6>
                            </div>
                        </Grid>

                        <Grid size={{ xs: 12 }} className="form-text-field">
                            <TextField
                                label="Notes / Special Conditions"
                                id="notes"
                                multiline
                                rows={3}
                                value={formData.notes ?? ''}
                                onChange={(e) => handleChange('notes', e.target.value)}
                            />
                        </Grid>

                    </Grid>

                    {/* Hidden submit target */}
                    <button ref={submitButtonRef} type="submit" className="hidden" />
                </div>
            </form>

            {/* SUBMIT BUTTON */}
            <div className="submit-buttons pt-3">
                <button
                    type="button"
                    disabled={isLoading}
                    className="btn btn-secondary custom-btn"
                    onClick={handleSubmitWrapper}
                >
                    {isExistingRecord ? 'Update' : 'Save'}
                </button>
            </div>

            {/* AUTO-RAISE CONFIRMATION DIALOG */}
            <Dialog open={showAutoRaiseConfirmDialog} onClose={handleCloseDialog}>
                <DialogContent>
                    <DialogContentText className="text-center">
                        {pendingAutoRaiseValue
                            ? 'Enabling auto-raise will allow the system to automatically raise a claim request on the maturity date. Do you want to continue?'
                            : 'Disabling auto-raise means the claim request will not be raised automatically. Do you want to continue?'}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <div className="d-flex align-items-center justify-content-center gap-2 w-100">
                        <button type="button" className="btn btn-primary custom-btn" onClick={handleConfirmAutoRaise}>
                            Confirm
                        </button>
                        <button type="button" className="btn btn-danger custom-btn" onClick={handleCloseDialog}>
                            Cancel
                        </button>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default withAuth(SBLCFormV2)([
    roles[2], roles[4], roles[5],
    roles[10], roles[11], roles[13], roles[14],
    roles[16], roles[17]
]);